import { useEffect, useState } from 'react';
import { submitCurrentGradeItem } from 'utils/gradeActions';
import { PRODUCT_CONDITION_TYPES } from 'utils/constants';
import { useProductStore, useReceiptStore } from 'store';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { cn } from 'utils/classnames';

export default function GradeItem() {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    currentProduct: productLine,
    isProductNew,
    setProductNew,
    productCondition,
    setProductCondition,
  } = useProductStore();
  const { receiptData } = useReceiptStore();
  const [isRejected, setIsRejected] = useState<boolean>(false);

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    const result = await submitCurrentGradeItem(isRejected);

    if (!result.success) {
      setLoading(false);
    }
  };

  const isUnexpected = productLine?.expected_ind === 'n';

  const optionButtonClass = (active: boolean, tone: 'blue' | 'yellow') =>
    cn(
      'h-11 flex-1 rounded-xl border-0 text-sm font-bold shadow-sm',
      active
        ? tone === 'blue'
          ? 'bg-blue-600 text-white hover:bg-blue-700'
          : 'bg-amber-400 text-black hover:bg-amber-500'
        : 'bg-slate-200 text-black hover:bg-slate-300'
    );

  useEffect(() => {
    setProductCondition(null);
    setProductNew(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Grade Item
      </CardTitle>
      {isUnexpected && (
        <CardTitle className="mb-1 text-center font-bold text-red-600">UNEXPECTED</CardTitle>
      )}

      <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Tracking #</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Article #</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.product_id === 'Unknown' ? 'N/A' : productLine?.product_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Desc:</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.product_id === 'Unknown' ? 'N/A' : productLine?.description || 'N/A'}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">Color/Size:</span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.product_id === 'Unknown'
              ? 'N/A'
              : (productLine?.style_color || 'N/A') + ' - ' + (productLine?.product_size || 'N/A')}
          </span>
        </div>
      </div>

      <div className="mb-4 mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-2.5 text-sm font-bold text-slate-700">Is New?</div>
        <div className="flex gap-2.5">
          <Button
            type="button"
            onClick={() => setProductNew(true)}
            disabled={loading}
            className={optionButtonClass(isProductNew === true, 'blue')}
          >
            Yes
          </Button>
          <Button
            type="button"
            onClick={() => setProductNew(false)}
            disabled={loading}
            className={optionButtonClass(isProductNew === false, 'yellow')}
          >
            No
          </Button>
        </div>
      </div>

      {isProductNew === true && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2.5 text-sm font-bold text-slate-700">Has Packaging?</div>
          <div className="flex gap-2.5">
            <Button
              type="button"
              onClick={() => setProductCondition(PRODUCT_CONDITION_TYPES.HAS_PACKAGING)}
              disabled={loading}
              className={optionButtonClass(
                productCondition === PRODUCT_CONDITION_TYPES.HAS_PACKAGING,
                'blue'
              )}
            >
              Yes
            </Button>
            <Button
              type="button"
              onClick={() => setProductCondition(PRODUCT_CONDITION_TYPES.NO_PACKAGING)}
              disabled={loading}
              className={optionButtonClass(
                productCondition === PRODUCT_CONDITION_TYPES.NO_PACKAGING,
                'yellow'
              )}
            >
              No
            </Button>
          </div>
        </div>
      )}

      {isProductNew === false && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2.5 text-sm font-bold text-slate-700">Product Condition:</div>
          <div className="flex gap-2.5">
            <Button
              type="button"
              onClick={() => setProductCondition(PRODUCT_CONDITION_TYPES.USABLE)}
              disabled={loading}
              className={optionButtonClass(
                productCondition === PRODUCT_CONDITION_TYPES.USABLE,
                'blue'
              )}
            >
              Usable
            </Button>
            <Button
              type="button"
              onClick={() => setProductCondition(PRODUCT_CONDITION_TYPES.UNUSABLE)}
              disabled={loading}
              className={optionButtonClass(
                productCondition === PRODUCT_CONDITION_TYPES.UNUSABLE,
                'yellow'
              )}
            >
              Unusable
            </Button>
          </div>
        </div>
      )}
      <div className="buttons-container">
        <Button
          type="button"
          onClick={() => setIsRejected((x) => !x)}
          variant={isRejected ? 'destructive' : 'outline'}
          className={cn('h-12 flex-1 ', {
            'border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700': !isRejected,
          })}
        >
          Reject
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading || isProductNew === null || productCondition === null}
          className="h-12 flex-1 bg-emerald-600 px-7 text-base font-bold text-white shadow-[0_4px_12px_rgba(40,167,69,0.35)] hover:bg-emerald-700 "
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </div>
  );
}
